import { useEffect, useRef, useState } from "react";
import type { NormalizedFeed } from "@kmitl/shared";

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";

export interface CityRef {
  id: string;
  city: string;       // display name (EN)
  countryShort: string;
  tz: string;         // IANA zone, e.g. "Asia/Bangkok"
  lat: number;
  lng: number;
}

/**
 * Partner cities the dashboard surfaces.
 * Order matters — Bangkok is always first (host), then ASEAN/East-Asia partners,
 * then EU, then US (East then West).
 */
export const PARTNER_CITIES: CityRef[] = [
  { id: "bkk", city: "Bangkok",   countryShort: "TH", tz: "Asia/Bangkok",       lat: 13.7395, lng: 100.5328 },
  { id: "sgp", city: "Singapore", countryShort: "SG", tz: "Asia/Singapore",     lat: 1.3521,  lng: 103.8198 },
  { id: "tok", city: "Tokyo",     countryShort: "JP", tz: "Asia/Tokyo",         lat: 35.6762, lng: 139.6503 },
  { id: "sha", city: "Shanghai",  countryShort: "CN", tz: "Asia/Shanghai",      lat: 31.2304, lng: 121.4737 },
  { id: "ber", city: "Berlin",    countryShort: "DE", tz: "Europe/Berlin",      lat: 52.5200, lng: 13.4050  },
  { id: "bos", city: "Boston",    countryShort: "US", tz: "America/New_York",   lat: 42.3601, lng: -71.0589 },
  { id: "sfo", city: "San Francisco", countryShort: "US", tz: "America/Los_Angeles", lat: 37.7749, lng: -122.4194 },
];

export interface CityWeather {
  city: CityRef;
  // current
  tempC: number | null;
  apparentTempC: number | null;
  humidity: number | null;
  rainNow: number | null;   // mm in current hour
  windKmh: number | null;
  windDeg: number | null;
  uv: number | null;
  cloudPct: number | null;
  pressurehPa: number | null;
  visKm: number | null;
  isDay: boolean | null;
  condition: string;
  sunrise: string | null;
  sunset: string | null;
  // 5-day forecast
  daily: Array<{
    date: string;          // YYYY-MM-DD
    tempMaxC: number;
    tempMinC: number;
    precipMm: number;
    precipProb: number;    // 0..100
  }>;
  // Hourly forecast (today + tomorrow) — for WorldStrip rain bar
  hourly: Array<{
    time: string;
    tempC: number;
    precipMm: number;
    precipProb: number;
    weatherCode: number;
  }>;
  fetchedAt: string;
}

async function fetchWorldWeather(signal?: AbortSignal): Promise<CityWeather[]> {
  const res = await fetch(`${API_BASE}/api/world-weather`, { signal, cache: "no-store" });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  const feed = (await res.json()) as NormalizedFeed<CityWeather>;
  return feed.features;
}

// 30-minute localStorage cache so a refresh isn't a fresh 7-city fan-out
const CACHE_KEY = "kmitl:world-weather";
const CACHE_TTL_MS = 30 * 60 * 1000;

function readCache(): { fetchedAt: string; results: CityWeather[] } | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { fetchedAt: string; results: CityWeather[] };
    if (!Array.isArray(parsed.results) || parsed.results.length === 0) return null;
    if (Date.now() - new Date(parsed.fetchedAt).getTime() > CACHE_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}
function writeCache(results: CityWeather[]) {
  try {
    window.localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ fetchedAt: new Date().toISOString(), results }),
    );
  } catch {}
}

export function useWorldWeather(): CityWeather[] {
  const [cities, setCities] = useState<CityWeather[]>(() => readCache()?.results ?? []);
  const hasSetFromCache = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let ctrl = new AbortController();
    const load = async () => {
      const cached = readCache();
      if (cached) {
        if (!hasSetFromCache.current) {
          hasSetFromCache.current = true;
          setCities(cached.results);
        }
        return;
      }
      const results = await fetchWorldWeather(ctrl.signal).catch(() => []);
      if (cancelled) return;
      hasSetFromCache.current = true;
      setCities(results);
      if (results.length > 0) writeCache(results);
    };
    load();
    const id = setInterval(() => {
      ctrl.abort();
      ctrl = new AbortController();
      load();
    }, CACHE_TTL_MS);
    return () => {
      cancelled = true;
      ctrl.abort();
      clearInterval(id);
    };
  }, []);

  return cities;
}
